import { Form, FormInstance, Input, Radio, Select } from 'antd';
import { Store } from 'antd/lib/form/interface';
import React from 'react';

import { useMutation } from '@common/hooks';
import { ComplianceItemUploadInput } from '@onboarding/components/compliance-item-files-list';
import { ComplianceItemInputExpirationDate } from '@onboarding/components/compliance-item-input-expiration-date';
import { ComplianceItemType } from '@onboarding/graphql';
import { StateList } from 'works/components/candidate-form/license-helper';
import { LicenseTypeInput } from 'works/components/candidate-form/license-type-input';
import {
  CreateLicenseMutation,
  UpdateLicenseMutation,
} from 'works/graphql/license';
import { formatToYearMonthDay } from 'works/legacy/lib/date-helper';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import { createMessage } from 'works/legacy/lib/message-helper';

function getStateCodeFromName(stateFullName: string) {
  return StateList[stateFullName as keyof typeof StateList];
}

type Props = {
  form: FormInstance;
  complianceItem: ComplianceItemType;
  refetchQueries: string[];
  onSubmit?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
};

export function ComplianceItemAddAttachmentFormLicense({
  form,
  complianceItem,
  refetchQueries,
  onSubmit,
  onSuccess,
  onError,
}: Props) {
  const { detailItem } = complianceItem;

  const errorMessage = () =>
    createMessage('error', 'Failed to update license. Please try again.');

  const [updateLicense, { loading: formSubmitting }] = useMutation(
    UpdateLicenseMutation,
    {
      refetchQueries,
      onCompleted: (response) => {
        if (response?.updateLicense.errors) {
          addErrorsToForm(form, response.updateLicense.errors);
          errorMessage();
          onError?.();
        } else {
          onSuccess?.();
          createMessage('success', 'License has been successfully updated.');
        }
      },
      onError: () => {
        errorMessage();
        onError?.();
      },
    }
  );

  const [createLicense] = useMutation(CreateLicenseMutation, {
    refetchQueries,
    onCompleted: (response) => {
      if (response?.createLicense.errors) {
        addErrorsToForm(form, response.createLicense.errors);
        errorMessage();
        onError?.();
      } else {
        onSuccess?.();
        createMessage('success', 'License has been successfully created.');
      }
    },
    onError: () => {
      errorMessage();
      onError?.();
    },
  });

  const onFinish = (values: Store) => {
    if (formSubmitting) return;

    onSubmit?.();

    if (!detailItem) {
      createLicense({
        variables: {
          worksListItemId: complianceItem.id,
          worksProfileId:
            complianceItem.worksList.staffingRequestAssignment.worksProfile.id,
          input: {
            number: values.number,
            compact: values.compact,
            expiresOn: formatToYearMonthDay(values.expiresOn),
            completedOn: formatToYearMonthDay(values.completedOn),
            licenseType: values.licenseType,
            stateCode: values.stateCode,
          },
          files: values.files ? values.files.fileList : null,
        },
      });

      return;
    }

    updateLicense({
      variables: {
        id: complianceItem.id,
        input: {
          completedOn: formatToYearMonthDay(values.completedOn),
          expiresOn: formatToYearMonthDay(values.expiresOn),
          licenseType: values.licenseType,
          number: values.number,
          stateCode: values.stateCode,
          compact: values.compact,
        },
        files: values.files?.fileList ?? [],
      },
    });
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      {detailItem?.name && (
        <Form.Item label="LicenseName">{detailItem.name}</Form.Item>
      )}
      <Form.Item
        label="License number"
        name="number"
        rules={[
          {
            required: true,
            message: 'Please enter a license number',
          },
        ]}
      >
        <Input
          size="large"
          disabled={formSubmitting}
          style={{ width: '100%' }}
        />
      </Form.Item>
      <LicenseTypeInput name="licenseType" submitting={formSubmitting} />
      <Form.Item
        label="State"
        name="stateCode"
        rules={[{ required: true, message: 'Please enter a license state' }]}
      >
        <Select size="large" disabled={formSubmitting}>
          {Object.keys(StateList).map((stateFullName) => {
            return (
              <Select.Option
                key={stateFullName}
                value={getStateCodeFromName(stateFullName)}
              >
                {stateFullName}
              </Select.Option>
            );
          })}
        </Select>
      </Form.Item>
      <Form.Item
        label="Is this a multistate or compact license (eNLC)?"
        name="compact"
        rules={[{ required: true, message: 'Please enter a license state' }]}
      >
        <Radio.Group>
          <Radio value={false}>No</Radio>
          <Radio value={true}>Yes</Radio>
        </Radio.Group>
      </Form.Item>
      <ComplianceItemInputExpirationDate
        expirationType={complianceItem.expirationType}
      />

      <ComplianceItemUploadInput multiple required />
    </Form>
  );
}
